import React, { useCallback, useContext } from 'react';
import ContextMenu from '@/components/ContextMenu';
import classNames from 'classnames';
import useContextMenu from 'react-use-context-menu';

import TableContext from './TableContext';
import { saveInvoke, ConditionalWrapper } from '@/utils';

/**
 * Table row context wrapper.
 */
function TableRowContextMenu({
  children,
  row,
}: {
  children: React.ReactNode;
  row: any;
}) {
  // Table context.
  const {
    props: { ContextMenu: ContextMenuContent },
    table,
  } = useContext(TableContext);

  const [
    bindMenu,
    bindMenuItem,
    useContextTrigger,
    { coords, setVisible, isVisible },
  ] = useContextMenu();

  const [bindTrigger] = useContextTrigger({
    collect: () => 'Title',
  });

  const handleClose = useCallback(() => {
    setVisible(false);
  }, [setVisible]);

  return (
    <div className="tr-context" {...bindTrigger}>
      {children}

      <ContextMenu
        bindMenu={bindMenu}
        isOpen={isVisible}
        coords={coords}
        onClosed={handleClose}
      >
        {ContextMenuContent && <ContextMenuContent {...table} row={row} />}
      </ContextMenu>
    </div>
  );
}

/**
 * Table row.
 */
export default function TableRow({
  row,
  className,
  style,
}: {
  row: any;
  className?: string;
  style?: React.CSSProperties;
}) {
  const {
    props: {
      TableCellRenderer,
      rowClassNames,
      ContextMenu: ContextMenuContent,
    },
  } = useContext(TableContext);

  const { key, ...rowProps } = row.getRowProps({
    className: classNames(
      'tr',
      { 'is-expanded': row.isExpanded && row.canExpand },
      saveInvoke(rowClassNames, row),
      className,
    ),
    style,
  });

  return (
    <div key={key} {...rowProps}>
      <ConditionalWrapper
        condition={ContextMenuContent}
        wrapper={TableRowContextMenu}
        row={row}
      >
        {row.cells.map((cell: any, index: number) => (
          <TableCellRenderer
            key={index}
            cell={cell}
            row={row}
            index={index + 1}
          />
        ))}
      </ConditionalWrapper>
    </div>
  );
}
